import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import * as SiIcons from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';

const { FiImage, FiShare2, FiTwitter, FiMessageCircle, FiMessageSquare, FiCopy, FiCheck } = FiIcons;
const { FaFacebookMessenger } = FaIcons;
const { SiViber } = SiIcons;

const ActionBar = ({ onDownload, shareData }) => {
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const baseUrl = "https://metabolic.sheker.com";
  const shareText = `My TDEE is ${shareData.tdee} kcal! Target for ${shareData.goalName.replace('_', ' ')}: ${shareData.goalCalories} kcal. Calculate yours at: ${baseUrl}`;
  
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(baseUrl);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(baseUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform) => {
    const links = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}`,
      whatsapp: `https://wa.me/?text=${encodedText}`,
      viber: `viber://forward?text=${encodedText}`,
      sms: `sms:?body=${encodedText}`,
      messenger: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
    };

    window.open(links[platform], '_blank');
  };

  const SocialButton = ({ icon, label, color, onClick, subtext }) => (
    <button 
      onClick={onClick} 
      className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl transition-colors text-left w-full group"
    >
      <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center text-white shadow-sm group-hover:scale-110 transition-transform`}>
        <SafeIcon icon={icon} className="w-4 h-4" />
      </div>
      <div className="flex flex-col">
        <span className="text-xs font-bold dark:text-gray-200">{label}</span>
        {subtext && <span className="text-[10px] text-gray-400 font-medium">{subtext}</span>}
      </div>
    </button>
  );

  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40">
      <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-2 flex items-center gap-1">
        <button 
          onClick={onDownload} 
          className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-blue-500/20"
        >
          <SafeIcon icon={FiImage} />
          <span>Save Blueprint</span>
        </button>

        <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 mx-1" />

        <div className="relative">
          <button 
            onClick={() => setShowShare(!showShare)} 
            className={`p-3 rounded-xl transition-all ${showShare ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            <SafeIcon icon={FiShare2} className="w-5 h-5" />
          </button>

          <AnimatePresence>
            {showShare && (
              <motion.div 
                initial={{ opacity: 0, y: 12, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.95 }}
                className="absolute bottom-full mb-4 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-[2rem] shadow-2xl p-3 flex flex-col gap-1 min-w-[220px]"
              >
                <div className="px-3 py-2 mb-1">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Share Results</span>
                </div>
                
                <SocialButton icon={FiTwitter} label="Twitter" color="bg-black" onClick={() => handleShare('twitter')} />
                <SocialButton icon={FiMessageCircle} label="WhatsApp" color="bg-green-500" onClick={() => handleShare('whatsapp')} />
                <SocialButton icon={FaFacebookMessenger} label="Messenger" color="bg-blue-500" onClick={() => handleShare('messenger')} />
                <SocialButton icon={SiViber} label="Viber" color="bg-purple-600" onClick={() => handleShare('viber')} />
                <SocialButton icon={FiMessageSquare} label="SMS Message" color="bg-gray-600" onClick={() => handleShare('sms')} />
                
                <div className="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2" />
                
                <SocialButton 
                  icon={copied ? FiCheck : FiCopy} 
                  label={copied ? "Link Copied!" : "Copy URL"} 
                  color={copied ? "bg-emerald-500" : "bg-blue-100 dark:bg-blue-900/50 !text-blue-600"} 
                  onClick={handleCopyLink}
                  subtext="metabolic.sheker.com"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ActionBar;